import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Modal,
  Page,
  PageHeader,
  Select,
  Skeleton,
  StartCard,
  StartCardList,
  StatusHint,
  Tabs,
} from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { CompanyInfo, PaymentHistoryItem, PaymentStatusFilter, TaxInvoiceStatus } from '../api/billingApi';

const PAGE_SIZE = 10;

const CURRENT_YEAR = new Date().getFullYear();

/** 영수증 공급자(인디피아) 사업자 정보 — 코드베이스 어디에도 실데이터가 없어(TU_COMPANY는
 *  공급받는자=스튜디오 정보만 관리) 화면단 상수로 고정한다(사용자 확인 값, 2026-09-16). */
const SUPPLIER_INFO = {
  bizNo: '144-81-05037',
  compName: '㈜와이앤씨스마트앱스',
  bizRepName: '윤석중',
};

const FILTER_TABS: { value: PaymentStatusFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'SUCCEEDED', label: '결제완료' },
  { value: 'REFUNDED', label: '환불' },
  { value: 'FAILED', label: '결제실패' },
];

/** 배지 색상(status)은 TC_CODE_LIST 소관이 아닌 순수 표시 스타일이라 계속 하드코딩한다.
 *  라벨 텍스트는 G01_PAYMENT_STATUS 공통코드 조회 결과로 덮어쓰고, 이 값은 조회 실패 시
 *  폴백으로만 쓰인다(billingApi.getPaymentStatusCodes 주석 참고). */
const STATUS_BADGE: Record<string, { label: string; status: 'success' | 'mute' | 'warning' }> = {
  SUCCEEDED: { label: '결제완료', status: 'success' },
  REFUNDED: { label: '환불완료', status: 'mute' },
  FAILED: { label: '결제실패', status: 'warning' },
};

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

/** 수정세금계산서는 음수 금액이라 "₩-1,000" 대신 "-₩1,000"으로 표기 */
function formatSignedWon(n: number) {
  return n < 0 ? `-₩${formatWon(-n)}` : `₩${formatWon(n)}`;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatDayLabel(iso: string) {
  const d = new Date(iso);
  const weekday = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${d.getMonth() + 1}.${d.getDate()} (${weekday})`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** BE는 하이픈 없는 원본값을 그대로 내려줌 — 사업자등록번호 표시 포맷(000-00-00000)은 FE 책임
 *  (PaymentMethodPage.tsx의 동일 헬퍼와 같은 로직 — 페이지 간 로컬 중복은 이 레포의 기존 관례) */
function formatBizNo(bizNo: string | null | undefined) {
  if (!bizNo) return '—';
  const digits = bizNo.replace(/\D/g, '');
  if (digits.length !== 10) return bizNo;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** 화면처리기준 §4: 환불도 새 줄로 별도 추가된 결제이력(TX_PAYMENT)이라, 이미 최신순으로 온 목록을
 *  순서대로 훑으면서 월이 바뀔 때만 새 그룹을 만들면 된다(재정렬 불필요). */
function groupByMonth(items: PaymentHistoryItem[]) {
  const groups: { label: string; items: PaymentHistoryItem[] }[] = [];
  for (const item of items) {
    const d = new Date(item.paidDtm);
    const label = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

/** 세금계산서(바로빌, 2026-10-15 연동) — 계좌이체 결제 + 사업자번호 등록 건만 발행 대상이라
 *  taxInvoiceStatus가 null이면 아무것도 보여주지 않는다(카드 결제는 매출전표=영수증이 증빙).
 *  발행 완료(ISSUED·SENT)일 때만 버튼으로 상세 모달을 열고, 그 외 상태는 안내 문구만 노출한다. */
const TAX_INVOICE_VIEWABLE: TaxInvoiceStatus[] = ['ISSUED', 'SENT'];

type TaxInvoiceHint = { tone: 'info' | 'muted' | 'warning'; text: string };

const TAX_INVOICE_HINT: Partial<Record<TaxInvoiceStatus, TaxInvoiceHint>> = {
  PENDING: { tone: 'info', text: '세금계산서 발행 준비 중' },
  CANCELED: { tone: 'muted', text: '세금계산서 발행 취소(환불)' },
  FAILED: { tone: 'warning', text: '세금계산서 발행 지연 · 고객센터 확인 중' },
  NTS_FAILED: { tone: 'warning', text: '세금계산서 발행 지연 · 고객센터 확인 중' },
};

/** 환불 행의 세금계산서는 수정세금계산서다 — 원본 행과 같은 문구를 쓰면 어느 문서 이야기인지 헷갈린다 */
const AMENDED_TAX_INVOICE_HINT: Partial<Record<TaxInvoiceStatus, TaxInvoiceHint>> = {
  PENDING: { tone: 'info', text: '수정세금계산서 발행 준비 중' },
  CANCELED: { tone: 'muted', text: '수정세금계산서 발행 취소' },
  FAILED: { tone: 'warning', text: '수정세금계산서 발행 지연 · 고객센터 확인 중' },
  NTS_FAILED: { tone: 'warning', text: '수정세금계산서 발행 지연 · 고객센터 확인 중' },
};

function taxInvoiceHint(item: PaymentHistoryItem): TaxInvoiceHint | undefined {
  if (!item.taxInvoiceStatus) return undefined;
  return (item.status === 'REFUNDED' ? AMENDED_TAX_INVOICE_HINT : TAX_INVOICE_HINT)[item.taxInvoiceStatus];
}

const TAX_INVOICE_STATUS_LABEL: Record<TaxInvoiceStatus, string> = {
  PENDING: '발행 준비 중',
  ISSUED: '발행 완료 · 국세청 전송 대기',
  SENT: '국세청 전송 완료',
  CANCELED: '발행 취소',
  FAILED: '발행 지연',
  NTS_FAILED: '발행 지연',
};

export function InvoiceHistoryPage() {
  const navigate = useNavigate();
  const [receiptItem, setReceiptItem] = useState<PaymentHistoryItem | null>(null);
  const [taxInvoiceItem, setTaxInvoiceItem] = useState<PaymentHistoryItem | null>(null);

  const companyInfoQuery = useQuery({
    queryKey: ['billing', 'companyInfo'],
    queryFn: async ({ signal }) => (await billingApi.getCompanyInfo(signal)).data.data,
  });

  // G01_PAYMENT_STATUS 공통코드 — 실패해도 STATUS_BADGE 하드코딩 라벨로 화면은 그대로 동작
  const statusCodesQuery = useQuery({
    queryKey: ['billing', 'paymentStatusCodes'],
    queryFn: async ({ signal }) => (await billingApi.getPaymentStatusCodes(signal)).data.data,
    staleTime: Infinity,
    retry: false,
  });
  const statusLabelByCode = useMemo(
    () => Object.fromEntries((statusCodesQuery.data ?? []).map((c) => [c.codeValue, c.codeName])),
    [statusCodesQuery.data],
  );
  const [year, setYear] = useState(CURRENT_YEAR);
  const [statusFilter, setStatusFilter] = useState<PaymentStatusFilter>('ALL');

  const summaryQuery = useQuery({
    queryKey: ['billing', 'paymentSummary'],
    queryFn: async ({ signal }) => (await billingApi.getPaymentSummary(signal)).data.data,
  });

  // 환불 신청 버튼 노출 조건 — SubscriptionManagePage.tsx·RefundRequestPage.tsx와 동일 규칙(2026-09-17
  // 정합 — 예전엔 alreadyUsed를 안 보고 refundEligible만 봐서, 이용해서 환불이 막힌 건에도 이
  // 링크가 보였다가 RefundRequestPage에서야 "환불이 어려워요"로 막히는 헛걸음이 있었다). 7일
  // 이내(refundEligible)이면서 서비스 미사용(!alreadyUsed)인 결제 건이 하나도 없으면 버튼 자체를
  // 숨긴다 — RefundRequestPage와 동일 쿼리키라 캐시 공유.
  const refundEligiblePaymentsQuery = useQuery({
    queryKey: ['billing', 'refundEligiblePayments'],
    queryFn: async ({ signal }) => (await billingApi.getRefundEligiblePayments(signal)).data.data,
  });
  const hasRefundEligiblePayment =
    refundEligiblePaymentsQuery.data?.some((p) => p.refundEligible && !p.alreadyUsed) ?? false;

  // 최초 결제연도(firstPaymentYear)부터 당해년도까지 최신순 — 결제 이력이 없으면 당해년도 1개만
  const yearOptions = useMemo(() => {
    const firstYear = Math.min(summaryQuery.data?.firstPaymentYear ?? CURRENT_YEAR, CURRENT_YEAR);
    const years: number[] = [];
    for (let y = CURRENT_YEAR; y >= firstYear; y--) years.push(y);
    return years;
  }, [summaryQuery.data?.firstPaymentYear]);

  const historyQuery = useInfiniteQuery({
    queryKey: ['billing', 'invoices', year, statusFilter],
    queryFn: async ({ pageParam, signal }) =>
      (
        await billingApi.getPaymentHistory({ year, status: statusFilter, offset: pageParam, limit: PAGE_SIZE }, signal)
      ).data.data,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length * PAGE_SIZE : undefined),
  });

  const items = useMemo(() => historyQuery.data?.pages.flatMap((p) => p.items) ?? [], [historyQuery.data]);
  const groups = useMemo(() => groupByMonth(items), [items]);

  // 무한스크롤 — 하단 센티넬이 뷰포트에 들어오면 자동으로 다음 페이지 조회 (TC-07-06, work-log 목록과 동일 패턴)
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !historyQuery.hasNextPage || historyQuery.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) historyQuery.fetchNextPage();
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [historyQuery.hasNextPage, historyQuery.isFetchingNextPage, historyQuery.fetchNextPage]);

  return (
    <Page width="lg">
      <PageHeader title="결제 내역·인보이스" onBack={() => navigate('/agents/billing/manage')} />

      {/* 요약 정보 (C-4-2) */}
      {summaryQuery.isLoading ? (
        <Skeleton className="mt-06" height={100} />
      ) : summaryQuery.isError ? (
        <ErrorState className="mt-06" title="결제 요약 정보를 불러오지 못했습니다" />
      ) : (
        <StartCardList className="mt-06">
          <StartCard label="누적 결제 금액" count={`₩${formatWon(summaryQuery.data?.netAmount)}`} />
          <StartCard label="결제 건수" count={summaryQuery.data?.paymentCount ?? 0} unit="건" />
          <StartCard label="마지막 결제일" count={formatDate(summaryQuery.data?.lastPaymentDtm)} />
        </StartCardList>
      )}

      {/* 필터·연도 선택 (C-4-3) */}
      <div className="mt-06 flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          variant="pill"
          tabs={FILTER_TABS}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as PaymentStatusFilter)}
        />
        <div className="flex items-center gap-sm">
          {hasRefundEligiblePayment && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/refund')}>
              환불 신청 →
            </Button>
          )}
          <Select
            aria-label="연도 선택"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}년
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* 결제 내역 리스트 (C-4-4, C-4-5) */}
      <div className="mt-06">
        {historyQuery.isLoading ? (
          <div className="flex flex-col gap-md">
            <Skeleton height={140} />
            <Skeleton height={140} />
          </div>
        ) : historyQuery.isError ? (
          <ErrorState title="결제 내역을 불러오지 못했습니다" />
        ) : groups.length === 0 ? (
          <EmptyState
            title="결제 내역이 없어요"
            description="아직 결제한 이력이 없어요. 요금제를 비교해보세요."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/agents/billing')}>
                요금제 비교하기
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-lg">
            {groups.map((group) => (
              <div key={group.label}>
                <h3 className="text-title-sm text-text">{group.label}</h3>
                <div className="mt-03 flex flex-col gap-sm">
                  {group.items.map((item) => {
                    const badge = STATUS_BADGE[item.status] ?? { label: item.status, status: 'mute' as const };
                    const badgeLabel = statusLabelByCode[item.status] ?? badge.label;
                    return (
                      <Card key={item.paymentId}>
                        <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-md">
                            <span className="text-body-sm text-text-muted">{formatDayLabel(item.paidDtm)}</span>
                            <div>
                              <p className="text-body-sm font-semibold text-text">{item.description}</p>
                              <p className="mt-01 text-caption text-text-muted">{item.paymentMethodLabel}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-md">
                            <Badge status={badge.status}>
                              {badgeLabel}
                            </Badge>
                            <span className="text-body-md font-bold text-text">
                              {item.status === 'REFUNDED' ? '-' : ''}₩{formatWon(item.amount)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-03 flex flex-wrap items-center gap-sm border-t border-border pt-03">
                          {/* 환불 행의 세금계산서는 수정세금계산서(마이너스 금액) — 원본 행과 버튼 이름으로 구분 */}
                          {item.taxInvoiceStatus && TAX_INVOICE_VIEWABLE.includes(item.taxInvoiceStatus) && (
                            <Button variant="ghost" size="sm" onClick={() => setTaxInvoiceItem(item)}>
                              {item.status === 'REFUNDED' ? '수정세금계산서' : '세금계산서'}
                            </Button>
                          )}
                          {item.status !== 'FAILED' && (
                            <Button variant="ghost" size="sm" onClick={() => setReceiptItem(item)}>
                              영수증
                            </Button>
                          )}
                          {(() => {
                            const hint = taxInvoiceHint(item);
                            return hint && <StatusHint tone={hint.tone}>{hint.text}</StatusHint>;
                          })()}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}

            {historyQuery.hasNextPage && (
              <div ref={sentinelRef} className="flex justify-center py-sm text-body-sm text-text-muted">
                {historyQuery.isFetchingNextPage ? '불러오는 중입니다.' : ''}
              </div>
            )}
          </div>
        )}
      </div>

      <ReceiptModal
        item={receiptItem}
        company={companyInfoQuery.data}
        statusLabel={receiptItem ? (statusLabelByCode[receiptItem.status] ?? STATUS_BADGE[receiptItem.status]?.label ?? receiptItem.status) : ''}
        onClose={() => setReceiptItem(null)}
      />

      <TaxInvoiceModal item={taxInvoiceItem} onClose={() => setTaxInvoiceItem(null)} />
    </Page>
  );
}

interface TaxInvoiceModalProps {
  item: PaymentHistoryItem | null;
  onClose: () => void;
}

/**
 * 세금계산서 상세(바로빌 발행분) — 법정 문서 원본은 바로빌 화면(viewUrl)으로 보여주고, 여기서는 요약만
 * 표시한다. 조회 URL은 유효시간이 문서화돼 있지 않아 모달을 열 때마다 새로 받고(gcTime 0), 팝업 차단을
 * 피하려고 비동기 조회 직후 자동으로 열지 않고 사용자가 "세금계산서 보기"를 눌러 연다.
 */
function TaxInvoiceModal({ item, onClose }: TaxInvoiceModalProps) {
  const taxInvoiceQuery = useQuery({
    queryKey: ['billing', 'taxInvoice', item?.paymentId],
    queryFn: async ({ signal }) => (await billingApi.getTaxInvoice(item!.paymentId, signal)).data.data,
    enabled: item != null,
    gcTime: 0,
    retry: false,
  });

  if (!item) return null;
  const invoice = taxInvoiceQuery.data;
  const amended = invoice?.invoiceType === 'AMENDED';

  return (
    // 로딩 중에도 버튼 이름과 같은 제목이 보이도록 결제 행 상태로 정한다(환불 행 = 수정세금계산서)
    <Modal
      open={item != null}
      onClose={onClose}
      title={amended || item.status === 'REFUNDED' ? '수정세금계산서' : '세금계산서'}
    >
      {taxInvoiceQuery.isLoading ? (
        <div className="flex flex-col gap-sm">
          <Skeleton height={20} />
          <Skeleton height={20} />
          <Skeleton height={20} />
        </div>
      ) : taxInvoiceQuery.isError || !invoice ? (
        <ErrorState
          title="세금계산서를 불러오지 못했습니다"
          description="잠시 후 다시 시도해주세요."
          onRetry={() => taxInvoiceQuery.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-md">
          <div className="flex flex-col gap-xs text-body-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">상태</span>
              <span className="text-text">{TAX_INVOICE_STATUS_LABEL[invoice.invoiceStatus]}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">작성일자</span>
              <span className="text-text">{formatDate(invoice.writeDate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">공급가액</span>
              <span className="text-text">{formatSignedWon(invoice.supplyAmt)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">세액</span>
              <span className="text-text">{formatSignedWon(invoice.taxAmt)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">국세청 승인번호</span>
              <span className="text-text">{invoice.ntsApprovalNo ?? '국세청 전송 후 표시'}</span>
            </div>
          </div>

          <div className="border-t border-border pt-03">
            <div className="flex items-center justify-between">
              <span className="text-body-md font-semibold text-text">합계</span>
              <span className="text-title-md font-bold text-text">{formatSignedWon(invoice.totalAmt)}</span>
            </div>
            {amended && (
              <p className="mt-02 text-caption text-text-muted">
                환불로 원래 세금계산서 금액을 차감하는 수정세금계산서예요.
              </p>
            )}
          </div>

          {/* 발행은 됐는데 조회 URL만 못 받은 경우(바로빌·연동 일시 장애) — BE가 요약은 그대로 주고 URL만 비운다 */}
          {!invoice.viewUrl && TAX_INVOICE_VIEWABLE.includes(invoice.invoiceStatus) && (
            <StatusHint tone="warning">세금계산서 화면을 불러오지 못했어요. 잠시 후 다시 열어주세요.</StatusHint>
          )}

          <Button
            variant="primary"
            className="w-full"
            disabled={!invoice.viewUrl}
            onClick={() => {
              if (invoice.viewUrl) window.open(invoice.viewUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            세금계산서 보기
          </Button>
        </div>
      )}
    </Modal>
  );
}

interface ReceiptModalProps {
  item: PaymentHistoryItem | null;
  company: CompanyInfo | undefined;
  statusLabel: string;
  onClose: () => void;
}

/**
 * 영수증(C-4) — 세금계산서와 달리 국세청에 신고되는 법정 문서가 아니라, 우리 DB에 이미 있는
 * 결제 정보(TX_PAYMENT)와 사업자 정보(TU_COMPANY)만으로 만들 수 있어 실제로 구현했다. 별도
 * PDF 라이브러리 없이 브라우저 인쇄(Ctrl+P → PDF로 저장)로 다운로드를 대신한다 — @media print로
 * 영수증 영역만 남기고 나머지(사이드바·다른 카드 등)는 인쇄 시 숨긴다.
 */
function ReceiptModal({ item, company, statusLabel, onClose }: ReceiptModalProps) {
  if (!item) return null;
  const refunded = item.status === 'REFUNDED';

  return (
    <Modal open={item != null} onClose={onClose} title="영수증">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #receipt-print-area, #receipt-print-area * { visibility: visible; }
          #receipt-print-area { position: absolute; top: 0; left: 0; width: 100%; padding: 24px; }
        }
      `}</style>
      <div id="receipt-print-area" className="flex flex-col gap-md">
        <div className="flex items-center justify-between">
          <span className="text-title-sm font-bold text-text">영수증</span>
          <Badge status={refunded ? 'mute' : 'success'}>
            {statusLabel}
          </Badge>
        </div>

        <div className="flex flex-col gap-xs text-body-sm">
          <div className="flex items-center justify-between">
            <span className="text-text-muted">결제일시</span>
            <span className="text-text">{formatDateTime(item.paidDtm)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-muted">상품</span>
            <span className="text-text">{item.description}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-muted">결제수단</span>
            <span className="text-text">{item.paymentMethodLabel}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-muted">승인번호</span>
            <span className="text-text">{item.pgTxnKey ?? '—'}</span>
          </div>
        </div>

        <div className="border-t border-border pt-03">
          <div className="flex items-center justify-between">
            <span className="text-body-md font-semibold text-text">{refunded ? '환불 금액' : '결제 금액'}</span>
            <span className="text-title-md font-bold text-text">
              {refunded ? '-' : ''}₩{formatWon(item.amount)}
            </span>
          </div>
        </div>

        <div className="border-t border-border pt-03">
          <p className="text-caption font-semibold text-text-muted">공급받는자</p>
          <div className="mt-02 flex flex-col gap-xs text-body-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">상호명</span>
              <span className="text-text">{company?.compName ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">사업자등록번호</span>
              <span className="text-text">{formatBizNo(company?.compNo)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">대표자명</span>
              <span className="text-text">{company?.compRepName ?? '—'}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-03">
          <p className="text-caption font-semibold text-text-muted">공급자</p>
          <div className="mt-02 flex flex-col gap-xs text-body-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">상호명</span>
              <span className="text-text">{SUPPLIER_INFO.compName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">사업자등록번호</span>
              <span className="text-text">{SUPPLIER_INFO.bizNo}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">대표자명</span>
              <span className="text-text">{SUPPLIER_INFO.bizRepName}</span>
            </div>
          </div>
        </div>
      </div>

      {/* #receipt-print-area 밖에 둔다 — 안에 있으면 @media print 규칙(#receipt-print-area *
          visibility: visible)에 걸려 인쇄 결과(PDF)에 이 버튼까지 함께 찍힌다(2026-09-16 발견·수정). */}
      <Button variant="primary" className="w-full" onClick={() => window.print()}>
        인쇄 · PDF로 저장
      </Button>
    </Modal>
  );
}
